import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileBarChart, Download, Calendar, Plus, RefreshCw, AlertCircle, FileText, Loader2, Clock, Zap } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ────────────────────────────────────────────────────────────────
// Professional client-ready Monthly Digital Marketing PDF
// ────────────────────────────────────────────────────────────────
const buildMonthlyReportPDF = ({ dashboardData, aiVisibility, addons, reportPeriod }) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;

  // Brand palette
  const primary = [91, 33, 182];         // deep violet
  const primaryLight = [139, 92, 246];   // violet
  const accent = [16, 185, 129];         // emerald
  const ink = [15, 23, 42];              // slate-900
  const body = [51, 65, 85];             // slate-700
  const muted = [100, 116, 139];         // slate-500
  const soft = [241, 245, 249];          // slate-100

  const firmName = dashboardData?.firm?.name || 'Client';
  const firmLogo = dashboardData?.firm?.logo || null;
  const generatedDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const periodLabel = reportPeriod || (() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return `${first.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${last.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  })();

  // ─── COVER PAGE ───────────────────────────────────────────────
  doc.setFillColor(...primary);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // Decorative circle
  doc.setFillColor(...primaryLight);
  doc.circle(pageWidth - 30, 40, 60, 'F');
  doc.setFillColor(255, 255, 255, 0.04);

  // Logo
  if (firmLogo) {
    try { doc.addImage(firmLogo, 'PNG', margin, 30, 32, 32); } catch (e) { /* ignore */ }
  }

  // Eyebrow
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255, 0.7);
  doc.setFontSize(10);
  doc.text('LAW AGENCY 360 · LAW FIRM DIGITAL MARKETING', margin, firmLogo ? 76 : 50);

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(30);
  doc.text('Monthly Digital Marketing', margin, pageHeight / 2 - 10);
  doc.text('Performance Report', margin, pageHeight / 2 + 3);

  // Client name
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255, 0.9);
  doc.text(firmName, margin, pageHeight / 2 + 20);

  // Period & date
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255, 0.7);
  doc.text(`Reporting period: ${periodLabel}`, margin, pageHeight - 40);
  doc.text(`Generated: ${generatedDate}`, margin, pageHeight - 33);

  // Footer branding
  doc.setDrawColor(255, 255, 255, 0.2);
  doc.line(margin, pageHeight - 22, pageWidth - margin, pageHeight - 22);
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255, 0.5);
  doc.text('Prepared by Law Agency 360 · Digital Marketing for Law Firms', margin, pageHeight - 14);

  // ─── INTERNAL PAGES ───────────────────────────────────────────
  doc.addPage();
  let yPos = 0;

  const drawPageHeader = () => {
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageWidth, 22, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text('Monthly Performance Report', margin, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255, 0.8);
    doc.text(firmName, pageWidth - margin, 14, { align: 'right' });
    yPos = 32;
  };

  const ensureSpace = (needed) => {
    if (yPos + needed > pageHeight - 22) {
      doc.addPage();
      drawPageHeader();
    }
  };

  const sectionHeading = (label) => {
    ensureSpace(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.setFontSize(14);
    doc.text(label, margin, yPos);
    // violet underline
    doc.setDrawColor(...primaryLight);
    doc.setLineWidth(0.7);
    doc.line(margin, yPos + 1.5, margin + 30, yPos + 1.5);
    yPos += 9;
  };

  const paragraph = (text) => {
    ensureSpace(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...body);
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);
    lines.forEach(l => {
      ensureSpace(6);
      doc.text(l, margin, yPos);
      yPos += 5.2;
    });
    yPos += 3;
  };

  const bulletList = (items) => {
    if (!items || !items.length) return;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...body);
    doc.setFontSize(10);
    items.forEach(item => {
      const lines = doc.splitTextToSize(item, pageWidth - 2 * margin - 6);
      ensureSpace(lines.length * 5 + 2);
      doc.setTextColor(...primaryLight);
      doc.text('•', margin, yPos);
      doc.setTextColor(...body);
      lines.forEach((l, idx) => {
        doc.text(l, margin + 5, yPos);
        yPos += 5.2;
        if (idx < lines.length - 1) ensureSpace(5);
      });
      yPos += 1;
    });
    yPos += 2;
  };

  const metricsTable = (rows) => {
    ensureSpace(24);
    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: primary, textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold' },
      bodyStyles: { fontSize: 10, textColor: ink, cellPadding: 3 },
      alternateRowStyles: { fillColor: soft },
      margin: { left: margin, right: margin },
    });
    yPos = doc.lastAutoTable.finalY + 8;
  };

  const dataTable = (head, rows) => {
    if (!rows || !rows.length) return;
    ensureSpace(24);
    autoTable(doc, {
      startY: yPos,
      head: [head],
      body: rows,
      theme: 'striped',
      headStyles: { fillColor: primary, textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { fontSize: 9, textColor: ink, cellPadding: 2.5 },
      margin: { left: margin, right: margin },
    });
    yPos = doc.lastAutoTable.finalY + 8;
  };

  drawPageHeader();

  // EXECUTIVE SUMMARY
  sectionHeading('Executive Summary');
  paragraph(
    `This report summarises the digital marketing work completed for ${firmName} during the ${periodLabel} reporting period, along with the key visibility improvements and the priorities for the period ahead.`
  );

  const stats = dashboardData?.stats || {};
  const work = dashboardData?.work_completed || {};
  const aiAggregate = aiVisibility?.aggregate_metrics || {};
  const totalCitations = aiAggregate.total_citations || dashboardData?.ai_visibility?.total_citations || 0;

  const wins = [
    totalCitations ? `${totalCitations} AI citation${totalCitations === 1 ? '' : 's'} captured across ChatGPT, Perplexity and Gemini.` : 'AI visibility foundation set for the period ahead.',
    stats.backlinks ? `${stats.backlinks} total backlinks now in your portfolio.` : 'Backlink outreach in motion.',
    stats.blogs_published ? `${stats.blogs_published} blog${stats.blogs_published === 1 ? '' : 's'} published this period.` : 'Content pipeline building for the next period.',
  ];
  bulletList(wins);

  // Work completed quick list
  const workList = [];
  if (work.blogs_created) workList.push(`${work.blogs_created} blog post${work.blogs_created === 1 ? '' : 's'} created.`);
  if (work.pages_optimized) workList.push(`${work.pages_optimized} page${work.pages_optimized === 1 ? '' : 's'} optimised for search.`);
  if (work.backlinks_added) workList.push(`${work.backlinks_added} new backlink${work.backlinks_added === 1 ? '' : 's'} built.`);
  if (work.technical_updates) workList.push(`${work.technical_updates} technical implementation${work.technical_updates === 1 ? '' : 's'} completed.`);
  if (workList.length) {
    sectionHeading('Work Completed This Period');
    bulletList(workList);
  }

  // AI VISIBILITY
  sectionHeading('AI Visibility');
  metricsTable([
    ['Total Citations', String(totalCitations)],
    ['ChatGPT Citations', String(aiAggregate.chatgpt_citations || dashboardData?.ai_visibility?.chatgpt_citations || 0)],
    ['Perplexity Citations', String(aiAggregate.perplexity_citations || dashboardData?.ai_visibility?.perplexity_citations || 0)],
    ['Gemini Citations', String(aiAggregate.gemini_citations || dashboardData?.ai_visibility?.gemini_citations || 0)],
    ['Last Updated', aiVisibility?.upload_date ? new Date(aiVisibility.upload_date).toLocaleDateString('en-GB') : '-'],
  ]);

  const topPrompts = (aiVisibility?.prompt_metrics || dashboardData?.ai_visibility?.top_prompts || []).slice(0, 6);
  if (topPrompts.length) {
    dataTable(
      ['Prompt', 'Platform', 'Position', 'Visibility %'],
      topPrompts.map((p, idx) => [
        (p.prompt || '').substring(0, 55) + ((p.prompt || '').length > 55 ? '…' : ''),
        p.platform || '-',
        `#${p.position || idx + 1}`,
        `${Number(p.visibility_score_pct || 0).toFixed(1)}%`,
      ])
    );
  }

  const competitors = aiVisibility?.competitors || [];
  if (competitors.length) {
    ensureSpace(8);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...ink); doc.setFontSize(11);
    doc.text('Competitive Snapshot', margin, yPos);
    yPos += 6;
    const compRows = [[firmName, String(totalCitations), 'You']];
    competitors.slice(0, 8).forEach(c => compRows.push([c.name, String(c.total_citations), '']));
    dataTable(['Firm', 'Total Citations', 'Note'], compRows);
  }

  // SEO PERFORMANCE
  sectionHeading('SEO Performance');
  const org = dashboardData?.organic_performance || {};
  metricsTable([
    ['Sessions', (org.sessions || 0).toLocaleString()],
    ['Total Users', (org.total_users || 0).toLocaleString()],
    ['Page Views', (org.page_views || 0).toLocaleString()],
    ['Total Keywords Tracked', String(stats.total_keywords || 0)],
    ['Active Keywords (Ranking)', String(stats.active_keywords || 0)],
    ['Average Position', String(stats.avg_position || '-')],
  ]);

  const topKeywords = (dashboardData?.top_keyword_movements || []).slice(0, 8);
  if (topKeywords.length) {
    ensureSpace(8);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...ink); doc.setFontSize(11);
    doc.text('Top Keyword Movements', margin, yPos);
    yPos += 6;
    dataTable(
      ['Keyword', 'Prev', 'Current', 'Change'],
      topKeywords.map(k => [
        (k.keyword || '').substring(0, 50),
        String(k.previous_position ?? '-'),
        String(k.current_position ?? '-'),
        String(k.change ?? '-'),
      ])
    );
  }

  // CONTENT & ASSETS
  sectionHeading('Content & Assets');
  metricsTable([
    ['Blogs Submitted', String(work.blogs_created || 0)],
    ['Blogs Approved', String(work.blogs_approved || stats.blogs_published || 0)],
    ['Pages Optimised', String(work.pages_optimized || 0)],
    ['Meta Updates', String(work.meta_updates || 0)],
    ['FAQs Added', String(work.faqs_added || 0)],
    ['Technical Implementations', String(work.technical_updates || 0)],
  ]);

  // LINK BUILDING
  sectionHeading('Link Building');
  metricsTable([
    ['Total Backlinks Built', String(stats.backlinks || 0)],
    ['New Backlinks This Period', String(work.backlinks_added || 0)],
    ['Backlinks Google Sheet', dashboardData?.firm?.backlinks_sheet_url ? 'See linked sheet below' : 'Not provided'],
  ]);
  if (dashboardData?.firm?.backlinks_sheet_url) {
    ensureSpace(8);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...primaryLight); doc.setFontSize(10);
    doc.textWithLink('Open Google Sheet ↗', margin, yPos, { url: dashboardData.firm.backlinks_sheet_url });
    yPos += 8;
  }

  // ADD-ONS
  const anyAddon = (addons?.social?.total || 0) > 0 || (addons?.ads?.campaigns || 0) > 0 || (addons?.videos?.total || 0) > 0;
  if (anyAddon) {
    sectionHeading('Add-ons');

    if (addons?.social?.total > 0) {
      ensureSpace(8);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(...ink); doc.setFontSize(11);
      doc.text('Social Media', margin, yPos); yPos += 6;
      metricsTable([
        ['Posts This Period', String(addons.social.total)],
        ['Approved', String(addons.social.approved)],
        ['Pending Approval', String(addons.social.pending)],
        ['Drafts', String(addons.social.drafts)],
      ]);
    }

    if (addons?.ads?.campaigns > 0) {
      ensureSpace(8);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(...ink); doc.setFontSize(11);
      doc.text('Paid Ads', margin, yPos); yPos += 6;
      metricsTable([
        ['Active Campaigns', String(addons.ads.campaigns)],
        ['Total Spend (£)', `£${Number(addons.ads.spend || 0).toLocaleString()}`],
        ['Impressions', Number(addons.ads.impressions || 0).toLocaleString()],
        ['Clicks', Number(addons.ads.clicks || 0).toLocaleString()],
        ['Leads Generated', String(addons.ads.leads || 0)],
        ['Avg. CTR', `${addons.ads.ctr || 0}%`],
        ['Avg. Cost per Lead', addons.ads.leads ? `£${addons.ads.cpl}` : '-'],
      ]);
    }

    if (addons?.videos?.total > 0) {
      ensureSpace(8);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(...ink); doc.setFontSize(11);
      doc.text('AI Video', margin, yPos); yPos += 6;
      metricsTable([
        ['Videos in Production', String(addons.videos.in_production)],
        ['Pending Approval', String(addons.videos.pending)],
        ['Delivered', String(addons.videos.delivered)],
      ]);
    }
  }

  // NEXT STEPS
  sectionHeading('Next Steps');
  const nextSteps = dashboardData?.strategic_next_steps || [];
  if (nextSteps.length) {
    bulletList(nextSteps);
  } else {
    paragraph('Strategic next actions will be confirmed with your account manager in the next review call.');
  }
  const pendingApprovals = [];
  if (addons?.social?.pending) pendingApprovals.push(`${addons.social.pending} social media post${addons.social.pending === 1 ? '' : 's'} awaiting your approval.`);
  if (addons?.ads?.pending_creatives) pendingApprovals.push(`${addons.ads.pending_creatives} ad creative${addons.ads.pending_creatives === 1 ? '' : 's'} awaiting your approval.`);
  if (addons?.videos?.pending) pendingApprovals.push(`${addons.videos.pending} video${addons.videos.pending === 1 ? '' : 's'} awaiting your approval.`);
  if (pendingApprovals.length) {
    ensureSpace(8);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...ink); doc.setFontSize(11);
    doc.text('Pending Approvals', margin, yPos); yPos += 6;
    bulletList(pendingApprovals);
  }

  // ─── Page footer with numbers (skip cover) ────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...soft); doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 16, pageWidth - margin, pageHeight - 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text('Law Agency 360 · Monthly Digital Marketing Performance Report', margin, pageHeight - 10);
    doc.text(`Page ${i - 1} of ${totalPages - 1}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  return doc;
};

export default function Reporting({ user, selectedClient }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [generatingAuto, setGeneratingAuto] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [aiVisibilityData, setAiVisibilityData] = useState(null);
  const [addonsData, setAddonsData] = useState(null);
  const [newReport, setNewReport] = useState({
    title: '',
    month: new Date().toISOString().slice(0, 7),
    summary: '',
    file_url: ''
  });

  const firmId = selectedClient?.firmId || user?.firm_id;
  const canEdit = user?.role && ['super_admin', 'internal_team'].includes(user.role);

  useEffect(() => {
    if (firmId) {
      fetchReports();
      fetchDashboardData();
      fetchAIVisibilityData();
      fetchAddonsData();
    }
  }, [firmId]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedClient 
        ? `${API}/admin/reports/${firmId}`
        : `${API}/reports`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedClient 
        ? `${API}/admin/client-dashboard/${firmId}`
        : `${API}/dashboard/overview`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  };

  const fetchAIVisibilityData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ai-visibility?firm_id=${firmId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.length > 0) {
        setAiVisibilityData(response.data[0]);
      }
    } catch (err) {
      console.error('Error fetching AI visibility data:', err);
    }
  };

  const fetchAddonsData = async () => {
    if (!firmId) return;
    const token = localStorage.getItem('token');
    const hdr = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [socialRes, campaignsRes, creativesRes, perfRes, videosRes] = await Promise.all([
        axios.get(`${API}/social/posts/${firmId}`, hdr).catch(() => ({ data: [] })),
        axios.get(`${API}/ads/campaigns/${firmId}`, hdr).catch(() => ({ data: [] })),
        axios.get(`${API}/ads/creatives/${firmId}`, hdr).catch(() => ({ data: [] })),
        axios.get(`${API}/ads/performance/${firmId}`, hdr).catch(() => ({ data: [] })),
        axios.get(`${API}/videos/${firmId}`, hdr).catch(() => ({ data: [] })),
      ]);
      const social = socialRes.data || [];
      const campaigns = campaignsRes.data || [];
      const creatives = creativesRes.data || [];
      const perf = perfRes.data || [];
      const videos = videosRes.data || [];

      const adsTotals = perf.reduce((acc, p) => {
        acc.spend += Number(p.spend || 0);
        acc.impressions += Number(p.impressions || 0);
        acc.clicks += Number(p.clicks || 0);
        acc.leads += Number(p.leads || 0);
        return acc;
      }, { spend: 0, impressions: 0, clicks: 0, leads: 0 });
      adsTotals.ctr = adsTotals.impressions ? ((adsTotals.clicks / adsTotals.impressions) * 100).toFixed(2) : 0;
      adsTotals.cpl = adsTotals.leads ? (adsTotals.spend / adsTotals.leads).toFixed(2) : 0;

      setAddonsData({
        social: {
          total: social.length,
          approved: social.filter(s => s.status === 'approved').length,
          pending: social.filter(s => s.status === 'pending_approval').length,
          drafts: social.filter(s => s.status === 'draft').length,
        },
        ads: {
          campaigns: campaigns.length,
          pending_creatives: creatives.filter(c => c.status === 'pending_approval').length,
          ...adsTotals,
        },
        videos: {
          total: videos.length,
          in_production: videos.filter(v => v.status === 'in_production').length,
          pending: videos.filter(v => v.status === 'pending_approval').length,
          delivered: videos.filter(v => v.status === 'delivered').length,
        },
      });
    } catch (err) { /* silent */ }
  };

  // Generate Automated Monthly Report
  const generateAutomatedReport = async () => {
    setGeneratingAuto(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/reports/generate/${firmId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchReports();
      await generatePDFForReport(response.data);
      alert('Monthly report generated successfully!');
    } catch (err) {
      console.error('Error generating automated report:', err);
      alert(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGeneratingAuto(false);
    }
  };

  const generatePDFForReport = async (reportData) => {
    if (!dashboardData) {
      alert('Dashboard data not loaded. Please refresh and try again.');
      return;
    }
    let periodLabel;
    if (reportData?.report_data?.period_start && reportData?.report_data?.period_end) {
      const s = new Date(reportData.report_data.period_start);
      const e = new Date(reportData.report_data.period_end);
      periodLabel = `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    const doc = buildMonthlyReportPDF({
      dashboardData, aiVisibility: aiVisibilityData, addons: addonsData, reportPeriod: periodLabel,
    });
    const firmName = (dashboardData.firm?.name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`${firmName}_Monthly_Report_${reportData?.month || new Date().toISOString().slice(0, 7)}.pdf`);
  };

  const generateExecutiveReport = async () => {
    if (!dashboardData) {
      alert('Dashboard data not loaded. Please refresh and try again.');
      return;
    }
    setGeneratingPDF(true);
    try {
      const doc = buildMonthlyReportPDF({
        dashboardData, aiVisibility: aiVisibilityData, addons: addonsData,
      });
      const firmName = (dashboardData.firm?.name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      doc.save(`${firmName}_Monthly_Report_${dateStr}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedClient 
        ? `${API}/admin/reports/${firmId}`
        : `${API}/reports`;
      
      await axios.post(endpoint, newReport, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowAddModal(false);
      setNewReport({ title: '', month: new Date().toISOString().slice(0, 7), summary: '', file_url: '' });
      fetchReports();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating report');
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '-';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  const formatPeriod = (report) => {
    const pd = report.report_data || {};
    if (pd.period_start && pd.period_end) {
      const s = new Date(pd.period_start);
      const e = new Date(pd.period_end);
      return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return formatMonth(report.month);
  };


  return (
    <div className="space-y-8 animate-fadeInUp" data-testid="reporting-page">
      {/* Header */}
      <div className="flex items-center justify-between opacity-0 animate-fadeInUp" style={{animationDelay: '0s', animationFillMode: 'forwards'}}>
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
            Monthly Reports
          </h1>
          <p className="text-slate-500 text-lg">Access your monthly SEO performance reports</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchReports}
            className="flex items-center gap-2 px-5 py-2.5 bg-white/70 backdrop-blur-sm border border-slate-200/50 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300"
          >
            <RefreshCw className="w-4 h-4 text-slate-600" />
            <span className="text-slate-700 font-medium">Refresh</span>
          </button>
          {canEdit && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300"
              data-testid="create-report-btn"
            >
              <Plus className="w-5 h-5" />
              Create Report
            </button>
          )}
        </div>
      </div>

      {/* Executive Report Download Card */}
      <div className="bg-gradient-to-r from-violet-500 to-indigo-600 rounded-2xl p-6 text-white soft-shadow-lg opacity-0 animate-fadeInUp" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}} data-testid="executive-report-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <FileBarChart className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-xl font-bold" style={{fontFamily: 'Outfit'}}>Executive Report</h3>
              <p className="text-white/80 text-sm">
                Generate a comprehensive PDF report with all your dashboard metrics
              </p>
            </div>
          </div>
          <button
            onClick={generateExecutiveReport}
            disabled={generatingPDF || !dashboardData}
            className="flex items-center gap-2 px-6 py-3 bg-white text-violet-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="download-executive-report-btn"
          >
            {generatingPDF ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download Executive Report (PDF)
              </>
            )}
          </button>
        </div>
      </div>

      {/* Automated Monthly Report Card - Internal Team Only */}
      {canEdit && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white soft-shadow-lg opacity-0 animate-fadeInUp" style={{animationDelay: '0.15s', animationFillMode: 'forwards'}} data-testid="automated-report-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Zap className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-xl font-bold" style={{fontFamily: 'Outfit'}}>Generate Monthly Report</h3>
                <p className="text-white/80 text-sm">
                  Auto-generate and store a monthly report for this billing cycle
                </p>
              </div>
            </div>
            <button
              onClick={generateAutomatedReport}
              disabled={generatingAuto || !dashboardData}
              className="flex items-center gap-2 px-6 py-3 bg-white text-emerald-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="generate-auto-report-btn"
            >
              {generatingAuto ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Clock className="w-5 h-5" />
                  Generate & Store Report
                </>
              )}
            </button>
          </div>
          <div className="mt-4 p-3 bg-white/10 rounded-lg">
            <p className="text-sm text-white/80 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Reports are automatically generated at each billing cycle (subscription renewal date)
            </p>
          </div>
        </div>
      )}

      {/* Info Notice */}
      <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200/50 rounded-2xl p-5 flex items-start gap-4 opacity-0 animate-fadeInUp soft-shadow" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}} data-testid="report-info">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-slate-800 font-semibold">Monthly Performance Reports</p>
          <p className="text-slate-600 text-sm mt-1">
            Reports are generated monthly by our team and include comprehensive analysis of your SEO performance, 
            keyword rankings, traffic data, and strategic recommendations.
          </p>
        </div>
      </div>

      {/* Reports Grid */}
      {loading ? (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-12 text-center soft-shadow-lg">
          <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto"></div>
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-12 text-center soft-shadow-lg opacity-0 animate-fadeInUp" style={{animationDelay: '0.3s', animationFillMode: 'forwards'}} data-testid="empty-reports">
          <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <FileBarChart className="w-10 h-10 text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>No Reports Available</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Monthly reports will appear here once they are generated by your account team.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-0 animate-fadeInUp" style={{animationDelay: '0.3s', animationFillMode: 'forwards'}} data-testid="reports-grid">
          {reports.map((report, idx) => (
            <div
              key={report.id}
              className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 hover-lift soft-shadow-lg"
              style={{animationDelay: `${(idx + 2) * 0.1}s`}}
              data-testid={`report-card-${report.id}`}
            >
              <div className="flex items-start justify-between mb-5">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                  <FileBarChart className="w-7 h-7 text-white" />
                </div>
                <span className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-semibold rounded-full shadow-sm">
                  Generated
                </span>
              </div>

              <h3 className="text-lg font-bold text-slate-900 mb-1" style={{fontFamily: 'Outfit'}}>
                {report.title}
              </h3>
              <div className="text-xs text-slate-500 mb-4">Monthly Digital Marketing Performance Report</div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Calendar className="w-4 h-4 text-amber-600" />
                  <span><span className="text-slate-400">Report month:</span> <span className="font-semibold text-slate-700">{formatMonth(report.month)}</span></span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-violet-500" />
                  <span><span className="text-slate-400">Period:</span> <span className="font-medium text-slate-700">{formatPeriod(report)}</span></span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span><span className="text-slate-400">Generated:</span> <span className="font-medium text-slate-700">{report.created_at ? new Date(report.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</span></span>
                </div>
              </div>

              {report.summary && (
                <p className="text-sm text-slate-600 mb-5 line-clamp-2">{report.summary}</p>
              )}

              <button
                onClick={() => generatePDFForReport(report)}
                disabled={!dashboardData}
                className="w-full flex items-center gap-2 justify-center px-5 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`download-report-${report.id}`}
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create Report Modal */}
      {showAddModal && canEdit && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 soft-shadow-lg animate-fadeInUp">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>
              Create Monthly Report
            </h2>
            
            <form onSubmit={handleCreateReport} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Report Title *</label>
                <input
                  type="text"
                  value={newReport.title}
                  onChange={(e) => setNewReport({...newReport, title: e.target.value})}
                  required
                  placeholder="e.g., January 2026 SEO Report"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Report Month *</label>
                <input
                  type="month"
                  value={newReport.month}
                  onChange={(e) => setNewReport({...newReport, month: e.target.value})}
                  required
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Summary *</label>
                <textarea
                  value={newReport.summary}
                  onChange={(e) => setNewReport({...newReport, summary: e.target.value})}
                  required
                  rows={4}
                  placeholder="Key highlights and performance summary..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">PDF URL (Optional)</label>
                <input
                  type="url"
                  value={newReport.file_url}
                  onChange={(e) => setNewReport({...newReport, file_url: e.target.value})}
                  placeholder="https://example.com/report.pdf"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-3 border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all"
                >
                  Create Report
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
